import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, addDoc, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../../config/firebase';
import { validateInviteCode } from '../../utils/inviteService';
import { RegisterWithInviteForm } from '../../types/business';
import { Business } from '../../types/business';
import toast from 'react-hot-toast';
import { AlertCircle, Loader2, Camera, User, X, Mail, Lock, Phone, UserPlus, CheckCircle, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { compressImage } from '../../utils/imageUtils';

interface CreateUserProps {
    preloadedBusiness?: Business | null;
}

const CreateUser: React.FC<CreateUserProps> = ({ preloadedBusiness }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(false);
    const [validationFailed, setValidationFailed] = useState(false);
    const [business, setBusiness] = useState<Business | null>(null);
    const [pendingApproval, setPendingApproval] = useState(false);
    const validationInProgress = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
    const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
    const [formData, setFormData] = useState<RegisterWithInviteForm>({
        inviteCode: searchParams.get('invite') || '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
    });
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error('Solo se permiten imágenes');
                return;
            }
            setProfilePhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setProfilePhoto(null);
        setProfilePhotoPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        // Si ya tenemos un negocio precargado, usarlo directamente
        if (preloadedBusiness) {
            setBusiness(preloadedBusiness);
            return;
        }

        // Intentar validar desde URL
        const inviteCode = searchParams.get('invite');
        if (inviteCode) {
            validateInvite(inviteCode);
            return;
        }
        const parts = location.pathname.split('/').filter(Boolean);
        const regIdx = parts.findIndex(p => p === 'register');
        if (regIdx >= 0 && parts.length > regIdx + 1) {
            const slug = parts[regIdx + 1];
            if (slug) {
                validateInvite(slug);
            }
        }
        // También verificar ruta /b/:slug
        const bIdx = parts.findIndex(p => p === 'b');
        if (bIdx >= 0 && parts.length > bIdx + 1) {
            const slug = parts[bIdx + 1];
            if (slug) {
                validateInvite(slug);
            }
        }
    }, [searchParams, location.pathname, preloadedBusiness]);

    const validateInvite = async (code: string) => {
        if (!code || validationInProgress.current) return;

        validationInProgress.current = true;
        setValidating(true);
        setValidationFailed(false);
        try {
            const businessData = await validateInviteCode(code);
            if (businessData) {
                setBusiness(businessData);
                setValidationFailed(false);
                toast.success(`Enlace válido para: ${businessData.name}`);
            } else {
                setValidationFailed(true);
                toast.error('Código de invitación inválido');
            }
        } catch (error: any) {
            setValidationFailed(true);
            toast.error(error.message || 'Error validando el código');
        } finally {
            setValidating(false);
            validationInProgress.current = false;
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        if (e.target.name === 'inviteCode') {
            setValidationFailed(false);
        }
    };

    const handleInviteCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (formData.inviteCode && !business && !validating) {
                validateInvite(formData.inviteCode);
            }
        }
    };

    const handleInviteCodeBlur = () => {
        if (formData.inviteCode && !business && !validating) {
            validateInvite(formData.inviteCode);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!business) {
            toast.error('Debes ingresar un código de invitación válido');
            return;
        }

        if (!acceptedTerms) {
            toast.error('Debes aceptar los términos y condiciones');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        if (formData.password.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        setLoading(true);

        try {
            // Determinar si requiere aprobación ANTES de crear el usuario
            const requiresApproval = (business as any).requireUserApproval === true;
            const initialStatus = requiresApproval ? 'pending_approval' : 'active';

            // Marcar que estamos en proceso de registro para evitar interferencia del listener
            if (requiresApproval) {
                sessionStorage.setItem('registration_in_progress', 'true');
            }

            const userCredential = await createUserWithEmailAndPassword(
                auth,
                formData.email,
                formData.password
            );
            const userId = userCredential.user.uid;

            let profilePhotoUrl = '';
            if (profilePhoto) {
                try {
                    const compressedPhoto = await compressImage(profilePhoto);
                    const photoRef = ref(storage, `profile-photos/${business.id}/${userId}/${Date.now()}_${compressedPhoto.name}`);
                    await uploadBytes(photoRef, compressedPhoto);
                    profilePhotoUrl = await getDownloadURL(photoRef);
                } catch (photoError) {
                    console.error('Error uploading profile photo:', photoError);
                }
            }

            // Crear el documento mientras el usuario está autenticado
            await setDoc(doc(db, 'users', userId), {
                businessId: business.id,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                profilePhotoUrl: profilePhotoUrl || '',
                role: 'user',
                status: initialStatus,
                membershipType: 'class_pass',
                createdAt: new Date(),
                updatedAt: new Date(),
            });

            if (requiresApproval) {
                // Crear notificación para el admin del negocio
                try {
                    const adminUserId = (business as any).adminUserId;
                    if (adminUserId) {
                        await addDoc(collection(db, 'notifications'), {
                            userId: adminUserId, // La notificación va dirigida al admin
                            businessId: business.id,
                            title: '👤 Nueva solicitud de registro',
                            message: `${formData.firstName} ${formData.lastName} solicita unirse a tu negocio y está pendiente de aprobación.`,
                            type: 'warning',
                            isRead: false,
                            createdAt: new Date(),
                            // Metadata adicional para identificar el tipo específico
                            metadata: {
                                type: 'pending_user_approval',
                                requestingUserId: userId,
                                requestingUserEmail: formData.email,
                                requestingUserName: `${formData.firstName} ${formData.lastName}`
                            }
                        });
                        console.log('📬 Notificación creada para admin:', adminUserId);
                    }
                } catch (notifError) {
                    console.error('Error creando notificación para admin:', notifError);
                    // No bloquear el registro si falla la notificación
                }

                // Limpiar bandera de registro
                sessionStorage.removeItem('registration_in_progress');
                // Cerrar sesión y mostrar pantalla de pendiente
                await auth.signOut();
                setLoading(false);
                setPendingApproval(true);
            } else {
                toast.success('¡Cuenta creada exitosamente!');
                // Usar replace para evitar que el usuario pueda volver al registro con el botón atrás
                window.location.replace('/dashboard');
            }
        } catch (error: any) {
            // Limpiar bandera de registro en caso de error
            sessionStorage.removeItem('registration_in_progress');
            console.error('Error creating user:', error);
            if (error.code === 'auth/email-already-in-use') {
                toast.error('Este email ya está registrado');
            } else {
                toast.error('Error al crear la cuenta: ' + error.message);
            }
            setLoading(false);
        }
    };

    // Pantalla de Pendiente de Aprobación
    if (pendingApproval) {
        return (
            <div className="text-center py-8 space-y-6">
                <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-10 h-10 text-amber-400" />
                </div>
                <div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                        ¡Cuenta Creada!
                    </h3>
                    <p className="text-amber-400 font-medium mb-4">
                        Pendiente de Aprobación
                    </p>
                    <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
                        Tu cuenta ha sido creada correctamente. El administrador de <span className="text-white font-medium">{business?.name}</span> debe aprobar tu acceso antes de que puedas entrar.
                    </p>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 max-w-sm mx-auto">
                    <p className="text-gray-400 text-sm">
                        📧 Te recomendamos contactar con el administrador para agilizar el proceso.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => window.location.replace('/login')}
                    className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors"
                >
                    Ir a Iniciar Sesión
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div>
                {/* Solo mostrar título y campo de código si NO hay negocio precargado */}
                {!preloadedBusiness && (
                    <>
                        <h3 className="text-xl font-semibold text-white mb-2">
                            Registro de Usuario
                        </h3>
                        <p className="text-gray-500 text-sm mb-6">
                            Introduce el código de invitación de tu gimnasio
                        </p>
                    </>
                )}

                <div className="space-y-4">
                    {/* Invite Code - solo mostrar si no hay negocio precargado */}
                    {!preloadedBusiness && (
                        <div>
                            <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-300 mb-2">
                                Código o Enlace de Invitación *
                            </label>
                            <div className="relative">
                                <input
                                    id="inviteCode"
                                    name="inviteCode"
                                    type="text"
                                    required
                                    value={formData.inviteCode}
                                    onChange={handleChange}
                                    onBlur={handleInviteCodeBlur}
                                    onKeyDown={handleInviteCodeKeyDown}
                                    disabled={validating || !!business}
                                    className="block w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:opacity-50 transition-all"
                                    placeholder="Pega aquí el enlace o código"
                                />
                                {validating && (
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                        <Loader2 className="h-5 w-5 text-amber-500 animate-spin" />
                                    </div>
                                )}
                            </div>
                            {validating && (
                                <p className="mt-2 text-sm text-amber-400 flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Validando código...
                                </p>
                            )}
                            {business && (
                                <div className="mt-3 p-4 bg-emerald-900/30 border border-emerald-700 rounded-xl">
                                    <p className="text-sm text-emerald-400 flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5" />
                                        Te registrarás en: <strong>{business.name}</strong>
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Only show form if business is validated */}
                    {business && (
                        <>
                            {/* Foto de perfil */}
                            <div className="flex flex-col items-center py-4">
                                <label className="block text-sm font-medium text-gray-300 mb-3">
                                    Foto de Perfil (opcional)
                                </label>
                                <div className="relative">
                                    {profilePhotoPreview ? (
                                        <div className="relative">
                                            <img
                                                src={profilePhotoPreview}
                                                alt="Preview"
                                                className="w-24 h-24 rounded-full object-cover border-4 border-amber-500/50"
                                            />
                                            <button
                                                type="button"
                                                onClick={removePhoto}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors shadow-lg"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-24 h-24 rounded-full bg-gray-800 border-4 border-dashed border-gray-600 flex items-center justify-center cursor-pointer hover:border-amber-500 hover:bg-gray-700 transition-all"
                                        >
                                            <div className="text-center">
                                                <Camera className="w-8 h-8 text-gray-500 mx-auto" />
                                                <span className="text-xs text-gray-500 mt-1 block">Añadir</span>
                                            </div>
                                        </div>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoSelect}
                                        className="hidden"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                                        Nombre *
                                    </label>
                                    <input
                                        id="firstName"
                                        name="firstName"
                                        type="text"
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="block w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                                        Apellido *
                                    </label>
                                    <input
                                        id="lastName"
                                        name="lastName"
                                        type="text"
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="block w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                                    Email *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-500" />
                                    </div>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                                    Teléfono
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Phone className="h-5 w-5 text-gray-500" />
                                    </div>
                                    <input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                                    Contraseña *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-500" />
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                        placeholder="Mínimo 6 caracteres"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                                    Confirmar Contraseña *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-500" />
                                    </div>
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        required
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                    />
                                </div>
                            </div>

                            {/* Checkbox de aceptación de términos */}
                            <div className="flex items-start gap-3">
                                <button
                                    type="button"
                                    onClick={() => setAcceptedTerms(!acceptedTerms)}
                                    className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all ${acceptedTerms
                                        ? 'bg-amber-500 border-amber-500'
                                        : 'bg-transparent border-gray-600 hover:border-amber-500'
                                        }`}
                                >
                                    {acceptedTerms && <Check className="w-3 h-3 text-white" />}
                                </button>
                                <label className="text-sm text-gray-400 leading-relaxed">
                                    He leído y acepto los{' '}
                                    <Link
                                        to="/terms"
                                        target="_blank"
                                        className="text-amber-400 hover:text-amber-300 underline"
                                    >
                                        Términos y Condiciones
                                    </Link>{' '}
                                    y la{' '}
                                    <Link
                                        to="/privacy"
                                        target="_blank"
                                        className="text-amber-400 hover:text-amber-300 underline"
                                    >
                                        Política de Privacidad
                                    </Link>
                                    . Consiento el tratamiento de mis datos personales según lo descrito.
                                </label>
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !acceptedTerms}
                                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl shadow-lg shadow-amber-500/25 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Creando cuenta...
                                    </>
                                ) : (
                                    <>
                                        <UserPlus className="w-5 h-5" />
                                        Crear Cuenta
                                    </>
                                )}
                            </button>
                        </>
                    )}

                    {!preloadedBusiness && !business && validationFailed && !validating && formData.inviteCode && (
                        <div className="p-4 bg-red-900/30 border border-red-700 rounded-xl flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-300">
                                El código de invitación no es válido. Verifica que lo hayas copiado correctamente.
                            </p>
                        </div>
                    )}

                    {!preloadedBusiness && !business && !validating && !validationFailed && formData.inviteCode && (
                        <p className="text-sm text-gray-500">
                            Pulsa Enter o haz clic fuera del campo para validar el código
                        </p>
                    )}
                </div>
            </div>
        </form>
    );
};

export default CreateUser;
